import React, { useRef } from 'react';
import CarouselDots from '../common/CarouselDots/CarouselDots';
import { useDotPager } from '../common/useDotPager';
import { useReachEnd } from '../common/useReachEnd';
import './HorizontalCarousel.scss';

export interface HorizontalCarouselProps {
  /** Each child becomes one snap slide. The trailing CTA is just another child. */
  children: React.ReactNode;
  /** Show pagination dots under the track. Defaults to true when >1 slide. */
  showDots?: boolean;
  /** Fired once per arrival near the track's right edge, so a caller can append
   *  the next page. Same contract as EdgeFadeScroller's, so a paged row keeps
   *  paging on the mobile carousel it swaps to. */
  onReachEnd?: () => void;
  className?: string;
  ariaLabel?: string;
}

/**
 * Reusable scroll-snap carousel for the Building view's Sellers/Buyers
 * carousels. Presentational only — slides are passed as children. The active dot
 * is derived from scroll position, so swiping and dot clicks stay in sync.
 */
const HorizontalCarousel: React.FC<HorizontalCarouselProps> = ({
  children,
  showDots,
  className,
  ariaLabel,
  onReachEnd,
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const slides = React.Children.toArray(children);
  const { slideCount, progress, hasOverflow, handleScroll, scrollToFraction } =
    useDotPager(trackRef, true, [slides.length]);
  useReachEnd(trackRef, onReachEnd, children);
  const dotsVisible = (showDots ?? slideCount > 1) && hasOverflow;

  return (
    <div className={`horizontal-carousel${className ? ` ${className}` : ''}`}>
      <div
        className="horizontal-carousel__track"
        ref={trackRef}
        onScroll={handleScroll}
        role="group"
        aria-label={ariaLabel}
      >
        {slides.map((slide, i) => (
          <div className="horizontal-carousel__slide" key={i}>
            {slide}
          </div>
        ))}
      </div>

      {dotsVisible && (
        <CarouselDots
          count={slideCount}
          progress={progress}
          onSeek={scrollToFraction}
        />
      )}
    </div>
  );
};

export default HorizontalCarousel;
